<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import { formatDisplayDateTime, nowApiDateTime } from '@yuksalish/ui'
import { useOperatorApi } from '../composables/useOperatorApi'
import type {
  DowntimeEvent,
  DowntimeReason,
  MachineAssignment,
  MachineCurrent,
  MachineFault,
  MachineRun,
  MachineRunCloseReason,
  MachineStateEvent,
  MachineStateValue,
  MachineStatus,
  MaintenanceRequest,
  MaterialRequirement,
  OperatorOrder,
  OperatorStage,
  OrderVariantOption,
  SetupParameter,
} from '../types/operator'

const route = useRoute()
const { t } = useI18n()
const auth = useAuthStore()
const {
  listMachines,
  listAssignments,
  getOrder,
  listStages,
  listDowntimeReasons,
  openDowntime,
  closeDowntime,
  listDowntime,
  getMachineSetup,
  saveMachineSetup,
  getMachineRequirements,
  listMachineFaults,
  reportMachineFault,
  resolveMachineFault,
  listMachineMaintenance,
  requestMachineMaintenance,
  updateMaintenanceStatus,
  recordMachineCounter,
  getMachineCurrent,
  listMachineRuns,
  listMachineStates,
  startMachineRun,
  changeoverMachineRun,
  closeMachineRun,
  setMachineState,
  listProductionOrders,
} = useOperatorApi()

const machineId = computed(() => Number(route.params.id))

const machine = ref<MachineStatus | null>(null)
const order = ref<OperatorOrder | null>(null)
const stages = ref<OperatorStage[]>([])
const assignments = ref<MachineAssignment[]>([])
const downtimeEvents = ref<DowntimeEvent[]>([])
const downtimeReasons = ref<DowntimeReason[]>([])
const setupParameters = ref<SetupParameter[]>([])
const requirements = ref<MaterialRequirement[]>([])
const faults = ref<MachineFault[]>([])
const maintenanceRequests = ref<MaintenanceRequest[]>([])
const requirementsLoading = ref(false)
const requirementsError = ref('')

const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const submitting = ref(false)

const showDowntime = ref(false)
const downtimeForm = reactive({ reason_code: '', notes: '' })

const showSetup = ref(false)
const setupForm = reactive<{ code: string; value: string }[]>([
  { code: 'needle_count', value: '' },
  { code: 'gauge', value: '' },
  { code: 'tension', value: '' },
  { code: 'yarn_feed', value: '' },
])
const setupNotes = ref('')

const showFault = ref(false)
const faultForm = reactive({ fault_code: '', description: '' })

const showMaintenance = ref(false)
const maintenanceForm = reactive({ reason: '', priority: 'medium', notes: '' })

const showCounter = ref(false)
const counterForm = reactive({ counter_total: 0, counter_shift: 0 })

// --- Machine runs / state timeline (MACHINE_RUNS_API.md) ---
const runCurrent = ref<MachineCurrent | null>(null)
const runs = ref<MachineRun[]>([])
const runsMeta = reactive({ page: 1, per_page: 10, total: 0, total_pages: 1 })
const machineStates = ref<MachineStateEvent[]>([])
const runLoading = ref(false)
const runError = ref('')
const STATE_VALUES: MachineStateValue[] = ['running', 'setup', 'idle', 'down', 'maintenance', 'blocked', 'off']
const CLOSE_REASONS: MachineRunCloseReason[] = ['completed', 'model_change', 'breakdown', 'shift_end', 'manual', 'order_cancelled']

const showStartRun = ref(false)
/** Active production orders the operator can manually pick on this machine. */
const orderChoices = ref<OperatorOrder[]>([])
const orderChoicesLoading = ref(false)
const startRunVariants = ref<OrderVariantOption[]>([])
const startRunStages = ref<OperatorStage[]>([])
const startRunForm = reactive({
  order_id: 0,
  variant_id: 0,
  stage_code: '',
  planned_quantity: 0,
  target_rate_per_hour: '',
  shift: 'day',
  notes: '',
})
const showChangeover = ref(false)
const changeoverVariants = ref<OrderVariantOption[]>([])
const changeoverStages = ref<OperatorStage[]>([])
const changeoverForm = reactive({
  order_id: 0,
  variant_id: 0,
  stage_code: '',
  target_rate_per_hour: '',
  close_reason: 'model_change' as MachineRunCloseReason,
  notes: '',
})
const showCloseRun = ref(false)
const closeRunForm = reactive({
  close_reason: 'shift_end' as MachineRunCloseReason,
  closing_counter_total: '',
  notes: '',
})
const stateForm = reactive({
  state: 'idle' as MachineStateValue,
  reason_code: '',
  notes: '',
})

const activeAssignments = computed(() => assignments.value.filter((assignment) => assignment.status === 'active'))
const canManage = computed(() => auth.user?.permissions.includes('production_orders.manage') ?? false)
const currentRun = computed(() => runCurrent.value?.run ?? null)
const currentState = computed(() => runCurrent.value?.state ?? null)
const runCounter = computed(() => runCurrent.value?.counter ?? null)
const activeAssignment = computed(() => activeAssignments.value[0] ?? null)
const assignedStage = computed(() => {
  const stageCode = activeAssignment.value?.stage_code
  return stageCode ? stages.value.find((stage) => stage.stage_code === stageCode) ?? null : null
})
const currentStage = computed(() => assignedStage.value ?? stages.value.find((stage) => stage.status !== 'completed') ?? stages.value[0] ?? null)
const plannedQuantity = computed(() => Number(order.value?.planned_quantity ?? currentStage.value?.planned_quantity ?? 0))
const producedQuantity = computed(() => Number(order.value?.produced_quantity ?? currentStage.value?.completed_quantity ?? 0))
const remainingQuantity = computed(() => Math.max(plannedQuantity.value - producedQuantity.value, 0))
const completionPercent = computed(() => {
  if (plannedQuantity.value <= 0) return 0
  return Math.min(100, Math.max(0, Math.round((producedQuantity.value / plannedQuantity.value) * 100)))
})
const assignedLot = computed(() => machine.value?.current_lot_number ?? activeAssignment.value?.lot_number ?? '')
const requiredSetupCodes = ['needle_count', 'gauge', 'tension', 'yarn_feed']
const setupByCode = computed(() => new Map(setupParameters.value.map((parameter) => [parameter.code, parameter])))
const completedSetupCount = computed(() => requiredSetupCodes.filter((code) => setupByCode.value.get(code)?.value).length)
const setupComplete = computed(() => completedSetupCount.value === requiredSetupCodes.length)
const currentStageRequirements = computed(() => {
  const stageCode = currentStage.value?.stage_code
  return stageCode ? requirements.value.filter((requirement) => requirement.stage_code === stageCode) : requirements.value
})
const materialTotals = computed(() => {
  const totals = new Map<string, number>()
  requirements.value.forEach((requirement) => {
    const unit = requirement.unit || ''
    totals.set(unit, (totals.get(unit) ?? 0) + Number(requirement.required_quantity || 0))
  })
  return [...totals.entries()].map(([unit, quantity]) => ({ unit, quantity }))
})
const plannedMaterialPairs = computed(() => {
  if (!requirements.value.length) return 0
  return Math.max(...requirements.value.map((requirement) => Number(requirement.planned_pairs || 0)), 0)
})
const openDowntimeEvents = computed(() => downtimeEvents.value.filter((event) => !event.end_time))
const closedDowntimeEvents = computed(() => downtimeEvents.value.filter((event) => event.end_time))
const openFaults = computed(() => faults.value.filter((fault) => fault.status === 'open'))
const resolvedFaults = computed(() => faults.value.filter((fault) => fault.status === 'resolved'))
const openMaintenanceRequests = computed(() => maintenanceRequests.value.filter((request) => request.status !== 'done'))
const doneMaintenanceRequests = computed(() => maintenanceRequests.value.filter((request) => request.status === 'done'))
const counterDelta = computed(() => ({
  total: Math.max(Number(counterForm.counter_total || 0) - Number(machine.value?.counter_total || 0), 0),
  shift: Math.max(Number(counterForm.counter_shift || 0) - Number(machine.value?.counter_shift || 0), 0),
}))
const workflowState = computed(() => {
  if (!order.value) return 'noOrder'
  if (!assignedLot.value) return 'missingLot'
  if (machine.value?.fault_active || machine.value?.status === 'down') return 'blocked'
  if (machine.value?.status === 'setup' || !setupComplete.value) return 'setup'
  return 'ready'
})

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listMachines({ page: 1, per_page: 100 })
    machine.value = result.items.find((m) => m.id === machineId.value) ?? null
    if (!machine.value) {
      error.value = t('operator.detail.error')
      return
    }
    await Promise.all([
      loadAssignments(),
      loadDowntime(),
      loadOrderContext(),
      loadSetup(),
      loadRequirements(),
      loadFaults(),
      loadMaintenance(),
      loadRunContext(),
    ])
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function loadAssignments() {
  try {
    const result = await listAssignments({ machine_id: machineId.value, page: 1, per_page: 50 })
    assignments.value = result.items
  } catch {
    assignments.value = []
  }
}

async function loadSetup() {
  try {
    setupParameters.value = await getMachineSetup(machineId.value, {
      order_id: machine.value?.current_order_id ?? undefined,
      lot_number: machine.value?.current_lot_number ?? undefined,
    })
  } catch {
    setupParameters.value = []
  }
}

async function loadRequirements() {
  requirementsLoading.value = true
  requirementsError.value = ''
  try {
    requirements.value = await getMachineRequirements(machineId.value, machine.value?.current_order_id ?? undefined)
  } catch (cause) {
    requirements.value = []
    requirementsError.value = formatApiError(cause)
  } finally {
    requirementsLoading.value = false
  }
}

async function loadFaults() {
  try {
    const result = await listMachineFaults(machineId.value, { page: 1, per_page: 50 })
    faults.value = result.items
  } catch {
    faults.value = []
  }
}

async function loadMaintenance() {
  try {
    const result = await listMachineMaintenance(machineId.value, { page: 1, per_page: 50 })
    maintenanceRequests.value = result.items
  } catch {
    maintenanceRequests.value = []
  }
}

async function loadDowntime() {
  try {
    const result = await listDowntime({ machine_id: machineId.value, page: 1, per_page: 50 })
    downtimeEvents.value = result.items
  } catch {
    downtimeEvents.value = []
  }
}

async function loadDowntimeReasons() {
  try {
    downtimeReasons.value = await listDowntimeReasons()
  } catch {
    downtimeReasons.value = []
  }
}

async function loadRunContext() {
  runLoading.value = true
  runError.value = ''
  try {
    runCurrent.value = await getMachineCurrent(machineId.value)
    const [runResult, stateResult] = await Promise.all([
      listMachineRuns(machineId.value, { page: runsMeta.page, per_page: runsMeta.per_page }),
      listMachineStates(machineId.value, { page: 1, per_page: 15 }),
    ])
    runs.value = runResult.items
    Object.assign(runsMeta, runResult.meta)
    machineStates.value = stateResult.items
  } catch (cause) {
    runError.value = formatApiError(cause)
  } finally {
    runLoading.value = false
  }
}

async function changeRunsPage(page: number) {
  runsMeta.page = Math.min(Math.max(1, page), Math.max(1, runsMeta.total_pages))
  await loadRunContext()
}

async function refreshRunContext() {
  // `load()` re-reads the machine, the run context, counters and assignments.
  await load()
}

function runVariantLabel(run: MachineRun | null): string {
  if (!run) return '—'
  return [run.color_name, run.size_label].filter(Boolean).join(' · ') || '—'
}

function runDuration(run: MachineRun): string {
  if (run.duration_minutes === null || run.duration_minutes === undefined) return '—'
  return t('operator.run.minutes', { count: Math.round(Number(run.duration_minutes)) })
}

function stateLabel(state: string): string {
  return t(`operator.run.states.${state}`)
}

function closeReasonLabel(reason: string | null | undefined): string {
  return reason ? t(`operator.run.closeReasons.${reason}`) : '—'
}

function openStartRun() {
  actionError.value = ''
  actionMessage.value = ''
  startRunForm.order_id = Number(machine.value?.current_order_id ?? order.value?.id ?? 0)
  startRunForm.variant_id = 0
  startRunForm.stage_code = ''
  startRunForm.planned_quantity = Number(currentStage.value?.planned_quantity ?? plannedQuantity.value) || 0
  startRunForm.target_rate_per_hour = ''
  startRunForm.shift = activeAssignment.value?.shift === 'night' ? 'night' : 'day'
  startRunForm.notes = ''
  startRunVariants.value = []
  startRunStages.value = []
  showStartRun.value = true
  void prepareStartRunOrder()
}

async function prepareStartRunOrder() {
  await loadOrderChoices()
  if (!orderChoices.value.some((item) => item.id === startRunForm.order_id) && orderChoices.value.length) {
    startRunForm.order_id = orderChoices.value[0].id
  }
  if (startRunForm.order_id) {
    await onStartRunOrderChange()
    // Default to the stage the machine is already assigned to, when it belongs to this order.
    const preferred = currentStage.value?.stage_code
    if (preferred && startRunStages.value.some((stage) => stage.stage_code === preferred)) {
      startRunForm.stage_code = preferred
    }
  }
}

function variantOptionLabel(variant: OrderVariantOption): string {
  return `${variant.color_name} · ${variant.size_label} (${Number(variant.planned_quantity).toLocaleString()})`
}

async function loadOrderChoices() {
  orderChoicesLoading.value = true
  try {
    const [active, planned] = await Promise.all([
      listProductionOrders({ status: 'in_progress', page: 1, per_page: 100 }),
      listProductionOrders({ status: 'planned', page: 1, per_page: 100 }),
    ])
    const seen = new Set<number>()
    orderChoices.value = [...active.items, ...planned.items].filter((item) => {
      if (seen.has(item.id)) return false
      seen.add(item.id)
      return true
    })
  } catch {
    orderChoices.value = []
  } finally {
    orderChoicesLoading.value = false
  }
}

async function loadOrderVariants(orderId: number): Promise<{ variants: OrderVariantOption[]; stages: OperatorStage[] }> {
  if (!orderId) return { variants: [], stages: [] }
  try {
    const [detail, stageRows] = await Promise.all([getOrder(orderId), listStages(orderId)])
    return { variants: detail.variants ?? [], stages: stageRows }
  } catch {
    return { variants: [], stages: [] }
  }
}

async function onStartRunOrderChange() {
  startRunForm.variant_id = 0
  const { variants, stages: stageRows } = await loadOrderVariants(Number(startRunForm.order_id))
  startRunVariants.value = variants
  startRunStages.value = stageRows
  startRunForm.stage_code = stageRows[0]?.stage_code ?? ''
}

function onStartRunVariantChange() {
  const variant = startRunVariants.value.find((item) => item.id === Number(startRunForm.variant_id))
  if (variant) startRunForm.planned_quantity = Number(variant.planned_quantity) || 0
}

async function submitStartRun() {
  actionError.value = ''
  if (!startRunForm.order_id) {
    actionError.value = t('operator.run.orderRequired')
    return
  }
  if (!startRunForm.variant_id) {
    actionError.value = t('operator.run.variantRequired')
    return
  }
  if (!startRunForm.stage_code) {
    actionError.value = t('operator.run.stageRequired')
    return
  }
  submitting.value = true
  try {
    await startMachineRun(machineId.value, {
      order_id: Number(startRunForm.order_id),
      stage_code: startRunForm.stage_code,
      variant_id: Number(startRunForm.variant_id),
      planned_quantity: Number(startRunForm.planned_quantity) || undefined,
      target_rate_per_hour: Number(startRunForm.target_rate_per_hour) || undefined,
      started_at: nowApiDateTime(),
      opening_counter_total: machine.value?.counter_total,
      operator_employee_id: activeAssignment.value?.employee_id ?? undefined,
      shift: startRunForm.shift,
      assignment_id: activeAssignment.value?.id,
      notes: startRunForm.notes.trim() || undefined,
    })
    showStartRun.value = false
    actionMessage.value = t('operator.run.started')
    await refreshRunContext()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

function openChangeover() {
  actionError.value = ''
  actionMessage.value = ''
  changeoverForm.order_id = currentRun.value?.production_order_id ?? 0
  changeoverForm.variant_id = 0
  changeoverForm.stage_code = currentRun.value?.stage_code ?? ''
  changeoverForm.target_rate_per_hour = currentRun.value?.target_rate_per_hour ? String(currentRun.value.target_rate_per_hour) : ''
  changeoverForm.close_reason = 'model_change'
  changeoverForm.notes = ''
  changeoverVariants.value = []
  changeoverStages.value = []
  showChangeover.value = true
  void prepareChangeoverOrder()
}

async function prepareChangeoverOrder() {
  await loadOrderChoices()
  if (!changeoverForm.order_id) return
  await onChangeoverOrderChange()
  const preferred = currentRun.value?.stage_code
  if (preferred && changeoverStages.value.some((stage) => stage.stage_code === preferred)) {
    changeoverForm.stage_code = preferred
  }
  // Pre-select the variant that is running now when the order stays the same.
  const running = changeoverVariants.value.find(
    (variant) =>
      variant.color_name === currentRun.value?.color_name &&
      variant.size_label === currentRun.value?.size_label,
  )
  if (running) changeoverForm.variant_id = running.id
}

async function onChangeoverOrderChange() {
  changeoverForm.variant_id = 0
  const { variants, stages: stageRows } = await loadOrderVariants(Number(changeoverForm.order_id))
  changeoverVariants.value = variants
  changeoverStages.value = stageRows
  changeoverForm.stage_code = stageRows[0]?.stage_code ?? ''
}

async function submitChangeover() {
  actionError.value = ''
  if (!currentRun.value) return
  if (!changeoverForm.order_id) {
    actionError.value = t('operator.run.orderRequired')
    return
  }
  if (!changeoverForm.variant_id) {
    actionError.value = t('operator.run.variantRequired')
    return
  }
  if (!changeoverForm.stage_code) {
    actionError.value = t('operator.run.stageRequired')
    return
  }
  submitting.value = true
  try {
    await changeoverMachineRun(machineId.value, currentRun.value.id, {
      order_id: Number(changeoverForm.order_id),
      stage_code: changeoverForm.stage_code,
      variant_id: Number(changeoverForm.variant_id),
      target_rate_per_hour: Number(changeoverForm.target_rate_per_hour) || undefined,
      // The old model stops now, the new one starts immediately: no setup gap.
      changeover_started_at: nowApiDateTime(),
      started_at: nowApiDateTime(),
      close_reason: changeoverForm.close_reason,
      closing_counter_total: machine.value?.counter_total,
      setup_reason_code: changeoverForm.close_reason,
      operator_employee_id: activeAssignment.value?.employee_id ?? undefined,
      notes: changeoverForm.notes.trim() || undefined,
    })
    showChangeover.value = false
    actionMessage.value = t('operator.run.changed')
    await refreshRunContext()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

function openCloseRun() {
  actionError.value = ''
  actionMessage.value = ''
  closeRunForm.close_reason = 'shift_end'
  closeRunForm.closing_counter_total = machine.value?.counter_total ? String(machine.value.counter_total) : ''
  closeRunForm.notes = ''
  showCloseRun.value = true
}

async function submitCloseRun() {
  actionError.value = ''
  if (!currentRun.value) return
  submitting.value = true
  try {
    await closeMachineRun(machineId.value, currentRun.value.id, {
      ended_at: nowApiDateTime(),
      close_reason: closeRunForm.close_reason,
      closing_counter_total: Number(closeRunForm.closing_counter_total) || undefined,
      notes: closeRunForm.notes.trim() || undefined,
    })
    showCloseRun.value = false
    actionMessage.value = t('operator.run.closed')
    await refreshRunContext()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

async function submitState() {
  actionError.value = ''
  submitting.value = true
  try {
    await setMachineState(machineId.value, {
      state: stateForm.state,
      started_at: nowApiDateTime(),
      reason_code: stateForm.reason_code.trim() || undefined,
      notes: stateForm.notes.trim() || undefined,
    })
    actionMessage.value = t('operator.run.stateChanged')
    stateForm.reason_code = ''
    stateForm.notes = ''
    await refreshRunContext()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

async function loadOrderContext() {
  const orderId = machine.value?.current_order_id
  if (!orderId) return
  try {
    ;[order.value, stages.value] = await Promise.all([
      getOrder(orderId),
      listStages(orderId),
    ])
  } catch {
    order.value = null
    stages.value = []
  }
}

onMounted(() => {
  void loadDowntimeReasons()
  void load()
})

function statusLabel(status: string): string {
  if (status === 'running') return t('operator.detail.running')
  if (status === 'idle') return t('operator.detail.idle')
  if (status === 'down') return t('operator.detail.down')
  if (status === 'setup') return t('operator.detail.setup')
  return status
}

function setupLabel(code: string, fallback = ''): string {
  const localized = t(`operator.detail.setupLabels.${code}`)
  return localized && !localized.startsWith('operator.detail.setupLabels') ? localized : (fallback || code)
}

function stageName(code: string): string {
  return stages.value.find((s) => s.stage_code === code)?.stage_name ?? code
}

function openDowntimeModal() {
  downtimeForm.reason_code = downtimeReasons.value[0]?.code ?? ''
  downtimeForm.notes = ''
  actionError.value = ''
  actionMessage.value = ''
  showDowntime.value = true
}

async function submitDowntime() {
  actionError.value = ''
  if (!downtimeForm.reason_code) {
    actionError.value = t('operator.detail.selectReason')
    return
  }
  submitting.value = true
  try {
    await openDowntime({
      reason_code: downtimeForm.reason_code,
      machine_id: machineId.value,
      order_id: machine.value?.current_order_id ?? null,
      lot_number: assignedLot.value || null,
      start_time: nowApiDateTime(),
      notes: downtimeForm.notes.trim() || undefined,
    })
    showDowntime.value = false
    actionMessage.value = t('operator.detail.opened')
    await Promise.all([loadDowntime(), load()])
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

async function closeEvent(event: DowntimeEvent) {
  actionError.value = ''
  if (!window.confirm(t('operator.detail.closeConfirm'))) return
  submitting.value = true
  try {
    await closeDowntime(event.id, { end_time: nowApiDateTime() })
    actionMessage.value = t('operator.detail.closed')
    await Promise.all([loadDowntime(), load()])
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

function openSetup() {
  const current = new Map(setupParameters.value.map((p) => [p.code, p.value]))
  setupForm.forEach((field) => { field.value = current.get(field.code) ?? '' })
  setupNotes.value = ''
  actionError.value = ''
  actionMessage.value = ''
  showSetup.value = true
}

async function submitSetup() {
  actionError.value = ''
  if (!machine.value?.current_order_id) {
    actionError.value = t('operator.detail.noOrder')
    return
  }
  if (!assignedLot.value) {
    actionError.value = t('operator.detail.setupNeedsLot')
    return
  }
  if (setupForm.some((field) => field.value.trim() === '')) {
    actionError.value = t('operator.detail.setupRequired')
    return
  }
  submitting.value = true
  try {
    setupParameters.value = await saveMachineSetup(machineId.value, {
      order_id: machine.value.current_order_id,
      lot_number: assignedLot.value,
      parameters: setupForm
        .filter((f) => f.value.trim() !== '')
        .map((f) => ({ code: f.code, value: f.value.trim() })),
      notes: setupNotes.value.trim() || undefined,
    })
    showSetup.value = false
    actionMessage.value = t('operator.detail.saved')
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

function openFault() {
  faultForm.fault_code = ''
  faultForm.description = ''
  actionError.value = ''
  actionMessage.value = ''
  showFault.value = true
}

async function submitFault() {
  actionError.value = ''
  if (!faultForm.fault_code || !faultForm.description) {
    actionError.value = t('operator.detail.noFaults')
    return
  }
  submitting.value = true
  try {
    await reportMachineFault(machineId.value, {
      fault_code: faultForm.fault_code.trim(),
      description: faultForm.description.trim(),
      order_id: machine.value?.current_order_id ?? null,
      lot_number: assignedLot.value || null,
    })
    showFault.value = false
    actionMessage.value = t('operator.detail.faultReported')
    await Promise.all([loadFaults(), load()])
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

async function resolveFault(fault: MachineFault) {
  actionError.value = ''
  if (!window.confirm(t('operator.detail.resolveConfirm'))) return
  submitting.value = true
  try {
    await resolveMachineFault(machineId.value, fault.id, {})
    actionMessage.value = t('operator.detail.resolved')
    await Promise.all([loadFaults(), load()])
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

function openMaintenance() {
  maintenanceForm.reason = ''
  maintenanceForm.priority = 'medium'
  maintenanceForm.notes = ''
  actionError.value = ''
  actionMessage.value = ''
  showMaintenance.value = true
}

async function submitMaintenance() {
  actionError.value = ''
  if (!maintenanceForm.reason) {
    actionError.value = t('operator.detail.reason')
    return
  }
  submitting.value = true
  try {
    await requestMachineMaintenance(machineId.value, {
      reason: maintenanceForm.reason.trim(),
      priority: maintenanceForm.priority,
      notes: maintenanceForm.notes.trim() || undefined,
    })
    showMaintenance.value = false
    actionMessage.value = t('operator.detail.requested')
    await loadMaintenance()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

async function advanceStatus(request: MaintenanceRequest) {
  actionError.value = ''
  const next = request.status === 'open' ? 'in_progress' : 'done'
  submitting.value = true
  try {
    await updateMaintenanceStatus(request.id, { status: next })
    actionMessage.value = t('operator.detail.advance')
    await loadMaintenance()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

function openCounter() {
  counterForm.counter_total = machine.value?.counter_total ?? 0
  counterForm.counter_shift = machine.value?.counter_shift ?? 0
  actionError.value = ''
  actionMessage.value = ''
  showCounter.value = true
}

async function submitCounter() {
  actionError.value = ''
  const total = Number(counterForm.counter_total) || 0
  const shift = Number(counterForm.counter_shift) || 0
  if (total < Number(machine.value?.counter_total || 0) || shift < Number(machine.value?.counter_shift || 0)) {
    actionError.value = t('operator.detail.counterCannotDecrease')
    return
  }
  submitting.value = true
  try {
    await recordMachineCounter(machineId.value, {
      counter_total: total,
      counter_shift: shift,
      recorded_at: nowApiDateTime(),
      client_event_id: `counter-${machineId.value}-${Date.now()}`,
    })
    showCounter.value = false
    actionMessage.value = t('operator.detail.counterRecorded')
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div class="page-heading">
    <RouterLink class="back" to="/machines">← {{ t('operator.detail.back') }}</RouterLink>
    <div>
      <p class="eyebrow">{{ t('operator.detail.eyebrow') }}</p>
      <h2>{{ machine?.name ?? t('operator.detail.title') }}</h2>
      <p>{{ t('operator.detail.title') }}</p>
    </div>
  </div>

  <!-- Loading -->
  <div v-if="loading" class="state-message"><p>{{ t('operator.detail.loading') }}</p></div>

  <!-- Error -->
  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('operator.detail.retry') }}</button>
  </div>

  <template v-else-if="machine">
    <!-- Status cards -->
    <div class="summary-row">
      <div class="summary-card">
        <span class="summary-value">{{ t(`operator.detail.${machine.status}`) }}</span>
        <span class="summary-label">{{ t('operator.detail.status') }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-value">{{ machine.counter_shift.toLocaleString() }}</span>
        <span class="summary-label">{{ t('operator.detail.counterShift') }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-value">{{ machine.counter_total.toLocaleString() }}</span>
        <span class="summary-label">{{ t('operator.detail.counterTotal') }}</span>
      </div>
      <div class="summary-card" :class="{ attention: machine.fault_active }">
        <span class="summary-value">{{ machine.fault_active ? t('operator.machines.activeFault') : t('operator.machines.noFault') }}</span>
        <span class="summary-label">{{ t('operator.machines.fault') }}</span>
      </div>
    </div>

    <div class="detail-grid">
      <!-- Machine run (MACHINE_RUNS_API.md) -->
      <section class="panel run-panel">
        <div class="panel-header">
          <h3>{{ t('operator.run.title') }}</h3>
          <div v-if="canManage" class="run-actions">
            <button v-if="currentRun" class="btn-secondary" :disabled="submitting" @click="openChangeover">{{ t('operator.run.changeover') }}</button>
            <button v-if="currentRun" class="btn-secondary" :disabled="submitting" @click="openCloseRun">{{ t('operator.run.closeRun') }}</button>
            <button v-else class="btn-primary" :disabled="submitting || !order" @click="openStartRun">{{ t('operator.run.startRun') }}</button>
          </div>
        </div>
        <p v-if="runError" class="error">{{ runError }}</p>
        <div v-if="runLoading && !runCurrent" class="empty-inline">{{ t('operator.state.loading') }}</div>
        <template v-else>
          <div v-if="currentRun" class="run-grid">
            <div><span>{{ t('operator.run.runCode') }}</span><strong>{{ currentRun.run_code }}</strong></div>
            <div><span>{{ t('operator.run.order') }}</span><strong>{{ order?.mo_code ?? `#${currentRun.production_order_id}` }}</strong></div>
            <div><span>{{ t('operator.run.variant') }}</span><strong>{{ runVariantLabel(currentRun) }}</strong></div>
            <div><span>{{ t('operator.run.stage') }}</span><strong>{{ stageName(currentRun.stage_code) }}</strong></div>
            <div><span>{{ t('operator.run.startedAt') }}</span><strong>{{ formatDisplayDateTime(currentRun.started_at) || currentRun.started_at }}</strong></div>
            <div><span>{{ t('operator.run.duration') }}</span><strong>{{ runDuration(currentRun) }}</strong></div>
            <div><span>{{ t('operator.run.planned') }}</span><strong>{{ Number(currentRun.planned_quantity).toLocaleString() }}</strong></div>
            <div><span>{{ t('operator.run.produced') }}</span><strong>{{ Number(currentRun.produced_quantity).toLocaleString() }}</strong></div>
            <div><span>{{ t('operator.run.accepted') }}</span><strong>{{ Number(currentRun.accepted_quantity).toLocaleString() }}</strong></div>
            <div><span>{{ t('operator.run.rejected') }}</span><strong>{{ Number(currentRun.rejected_quantity).toLocaleString() }}</strong></div>
            <div><span>{{ t('operator.run.targetRate') }}</span><strong>{{ currentRun.target_rate_per_hour ?? '—' }}</strong></div>
            <div><span>{{ t('operator.run.counter') }}</span><strong>{{ runCounter ? runCounter.counter_shift.toLocaleString() : '—' }}</strong></div>
          </div>
          <p v-else class="empty-inline">{{ t('operator.run.noRun') }}</p>

          <div class="state-bar">
            <div v-if="currentState">
              <span>{{ t('operator.run.machineState') }}</span>
              <strong>{{ stateLabel(currentState.state) }}</strong>
              <small>{{ formatDisplayDateTime(currentState.started_at) || currentState.started_at }}</small>
            </div>
            <div v-else class="empty-inline">{{ t('operator.run.noState') }}</div>
            <form v-if="canManage" class="state-form" @submit.prevent="submitState">
              <select v-model="stateForm.state">
                <option v-for="value in STATE_VALUES" :key="value" :value="value">{{ stateLabel(value) }}</option>
              </select>
              <input v-model="stateForm.reason_code" type="text" :placeholder="t('operator.run.reasonPlaceholder')" />
              <button class="btn-secondary" type="submit" :disabled="submitting">{{ t('operator.run.applyState') }}</button>
            </form>
          </div>

          <h4 class="run-heading">{{ t('operator.run.history') }}</h4>
          <p v-if="!runs.length" class="empty-inline">{{ t('operator.run.noRuns') }}</p>
          <table v-else class="data-table">
            <thead>
              <tr>
                <th>{{ t('operator.run.runCode') }}</th>
                <th>{{ t('operator.run.variant') }}</th>
                <th>{{ t('operator.run.startedAt') }}</th>
                <th>{{ t('operator.run.produced') }}</th>
                <th>{{ t('operator.run.status') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="run in runs" :key="run.id">
                <td>{{ run.run_code }}</td>
                <td>{{ runVariantLabel(run) }}</td>
                <td>{{ formatDisplayDateTime(run.started_at) || run.started_at }}</td>
                <td class="cell-qty">{{ Number(run.produced_quantity).toLocaleString() }}</td>
                <td>{{ run.is_open ? t('operator.run.statusOpen') : closeReasonLabel(run.close_reason) }}</td>
              </tr>
            </tbody>
          </table>
          <div v-if="runsMeta.total_pages > 1" class="run-pager">
            <button class="link-action" type="button" :disabled="runsMeta.page <= 1" @click="changeRunsPage(runsMeta.page - 1)">‹</button>
            <span>{{ runsMeta.page }} / {{ runsMeta.total_pages }}</span>
            <button class="link-action" type="button" :disabled="runsMeta.page >= runsMeta.total_pages" @click="changeRunsPage(runsMeta.page + 1)">›</button>
          </div>

          <h4 class="run-heading">{{ t('operator.run.stateHistory') }}</h4>
          <p v-if="!machineStates.length" class="empty-inline">{{ t('operator.run.noStates') }}</p>
          <table v-else class="data-table">
            <thead>
              <tr>
                <th>{{ t('operator.run.machineState') }}</th>
                <th>{{ t('operator.run.startedAt') }}</th>
                <th>{{ t('operator.run.endedAt') }}</th>
                <th>{{ t('operator.run.reason') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="event in machineStates" :key="event.id">
                <td>{{ stateLabel(event.state) }}</td>
                <td>{{ formatDisplayDateTime(event.started_at) || event.started_at }}</td>
                <td>{{ event.ended_at ? formatDisplayDateTime(event.ended_at) || event.ended_at : t('operator.run.statusOpen') }}</td>
                <td>{{ event.reason_code || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </template>
      </section>

      <!-- Assigned order -->
      <section class="panel">
        <h3>{{ t('operator.detail.currentOrder') }}</h3>
        <p v-if="!order" class="empty-inline">{{ t('operator.detail.noOrder') }}</p>
        <template v-else>
          <p class="order-code">{{ order.mo_code }}</p>
          <p class="order-meta">{{ order.product_name }} · {{ order.department_name }}</p>
          <p class="order-meta">
            {{ Number(order.produced_quantity || 0).toLocaleString() }} / {{ order.planned_quantity.toLocaleString() }}
          </p>
          <p v-if="machine.current_lot_number" class="order-meta">
            {{ t('operator.detail.currentLot') }}: {{ machine.current_lot_number }}
          </p>
          <div class="progress-stack">
            <div class="progress-head">
              <span>{{ t('operator.detail.orderProgress') }}</span>
              <strong>{{ completionPercent }}%</strong>
            </div>
            <progress :value="completionPercent" max="100" />
            <div class="order-stats">
              <span>{{ t('operator.detail.planned') }}: <strong>{{ plannedQuantity.toLocaleString() }}</strong></span>
              <span>{{ t('operator.detail.produced') }}: <strong>{{ producedQuantity.toLocaleString() }}</strong></span>
              <span>{{ t('operator.detail.remaining') }}: <strong>{{ remainingQuantity.toLocaleString() }}</strong></span>
            </div>
          </div>
        </template>
      </section>

      <!-- Operator workflow -->
      <section class="panel workflow-panel" :class="`workflow-${workflowState}`">
        <div class="panel-header">
          <h3>{{ t('operator.detail.workflow') }}</h3>
          <span class="workflow-badge">{{ t(`operator.detail.workflowStates.${workflowState}`) }}</span>
        </div>
        <div class="workflow-grid">
          <div>
            <span>{{ t('operator.detail.assignedStage') }}</span>
            <strong>{{ currentStage ? stageName(currentStage.stage_code) : '—' }}</strong>
          </div>
          <div>
            <span>{{ t('operator.detail.assignedLot') }}</span>
            <strong>{{ assignedLot || '—' }}</strong>
          </div>
          <div>
            <span>{{ t('operator.detail.assignedOperator') }}</span>
            <strong>{{ activeAssignment?.employee_name ?? '—' }}</strong>
          </div>
          <div>
            <span>{{ t('operator.detail.machineReadiness') }}</span>
            <strong>{{ t(`operator.detail.workflowStates.${workflowState}`) }}</strong>
          </div>
          <div>
            <span>{{ t('operator.detail.setupReadiness') }}</span>
            <strong>{{ t('operator.detail.setupReadinessValue', { count: completedSetupCount, total: requiredSetupCodes.length }) }}</strong>
          </div>
          <div>
            <span>{{ t('operator.detail.materialReadiness') }}</span>
            <strong>{{ t('operator.detail.materialReadinessValue', { count: requirements.length }) }}</strong>
          </div>
        </div>
        <p class="workflow-hint">{{ t(`operator.detail.workflowHints.${workflowState}`) }}</p>
      </section>

      <!-- Assignments -->
      <section class="panel">
        <h3>{{ t('operator.detail.assignments') }}</h3>
        <div v-if="activeAssignments.length === 0" class="empty-inline">{{ t('operator.detail.noAssignments') }}</div>
        <ul v-else class="plain-list">
          <li v-for="a in activeAssignments" :key="a.id">
            <strong>{{ a.employee_name ?? a.machine_name ?? '—' }}</strong>
            <span>{{ a.stage_name ?? a.stage_code }} · {{ a.order_code }}</span>
            <span>{{ t('operator.detail.shift') }}: {{ a.shift }}</span>
          </li>
        </ul>
      </section>

      <!-- Setup parameters -->
      <section class="panel">
        <div class="panel-header">
          <h3>{{ t('operator.detail.setupParameters') }}</h3>
          <button class="btn-secondary" :disabled="!order || !assignedLot" @click="openSetup">{{ t('operator.detail.saveSetup') }}</button>
        </div>
        <p class="setup-context">
          {{ t('operator.detail.setupContext') }}:
          <strong>{{ order?.mo_code ?? '—' }}</strong>
          <span>{{ t('operator.detail.currentLot') }}: <strong>{{ assignedLot || '—' }}</strong></span>
        </p>
        <div v-if="setupParameters.length === 0" class="empty-inline">{{ t('operator.detail.noSetup') }}</div>
        <table v-else class="data-table">
          <thead>
            <tr>
              <th>{{ t('operator.detail.setupParameters') }}</th>
              <th>{{ t('operator.detail.setupValue') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in setupParameters" :key="p.code">
              <td>{{ setupLabel(p.code, p.label) }}</td>
              <td class="cell-qty">{{ p.value }} {{ p.unit }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- Material requirements -->
      <section class="panel">
        <div class="panel-header">
          <h3>{{ t('operator.detail.materialRequirements') }}</h3>
          <button class="btn-secondary" :disabled="requirementsLoading || !order" @click="loadRequirements">{{ t('operator.detail.refreshMaterials') }}</button>
        </div>
        <p class="setup-context">
          {{ t('operator.detail.materialContext') }}:
          <strong>{{ order?.mo_code ?? '—' }}</strong>
          <span>{{ t('operator.detail.assignedStage') }}: <strong>{{ currentStage ? stageName(currentStage.stage_code) : '—' }}</strong></span>
          <span>{{ t('operator.detail.plannedPairs') }}: <strong>{{ (plannedMaterialPairs || plannedQuantity).toLocaleString() }}</strong></span>
        </p>
        <div v-if="requirementsLoading" class="empty-inline">{{ t('operator.detail.materialsLoading') }}</div>
        <div v-else-if="requirementsError" class="inline-error">
          <span>{{ requirementsError }}</span>
          <button class="link-action" @click="loadRequirements">{{ t('operator.detail.retry') }}</button>
        </div>
        <div v-else-if="requirements.length === 0" class="empty-inline">{{ t('operator.detail.noRequirements') }}</div>
        <div v-if="!requirementsLoading && !requirementsError && materialTotals.length" class="material-totals">
          <span v-for="total in materialTotals" :key="total.unit || 'unitless'">
            {{ t('operator.detail.totalRequired') }}:
            <strong>{{ total.quantity.toLocaleString() }} {{ total.unit }}</strong>
          </span>
        </div>
        <table v-if="!requirementsLoading && !requirementsError && currentStageRequirements.length" class="data-table material-table">
          <thead>
            <tr>
              <th>{{ t('operator.detail.material') }}</th>
              <th>{{ t('operator.detail.stage') }}</th>
              <th>{{ t('operator.detail.requiredQuantity') }}</th>
              <th>{{ t('operator.detail.requiredQuantity') }} ({{ t('operator.detail.perPair') }})</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, index) in currentStageRequirements" :key="`${r.material_id}-${index}`">
              <td>{{ r.material_name }}</td>
              <td>{{ r.stage_name }}</td>
              <td class="cell-qty">{{ r.required_quantity.toLocaleString() }} {{ r.unit }}</td>
              <td class="cell-qty">{{ r.per_pair }} {{ r.unit }}</td>
            </tr>
          </tbody>
        </table>
        <p v-if="!requirementsLoading && !requirementsError && requirements.length && currentStageRequirements.length === 0" class="empty-inline">
          {{ t('operator.detail.noStageRequirements') }}
        </p>
      </section>

      <!-- Downtime -->
      <section class="panel">
        <div class="panel-header">
          <h3>{{ t('operator.detail.downtime') }}</h3>
          <button class="btn-secondary" :disabled="openDowntimeEvents.length > 0" @click="openDowntimeModal">{{ t('operator.detail.reportDowntime') }}</button>
        </div>
        <div class="mini-stats">
          <span>{{ t('operator.detail.openItems', { count: openDowntimeEvents.length }) }}</span>
          <span>{{ t('operator.detail.closedItems', { count: closedDowntimeEvents.length }) }}</span>
        </div>
        <small v-if="actionError" class="error">{{ actionError }}</small>
        <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>
        <div v-if="downtimeEvents.length === 0" class="empty-inline">{{ t('operator.detail.noDowntime') }}</div>
        <table v-else class="data-table">
          <thead>
            <tr>
              <th>{{ t('operator.detail.currentOrder') }}</th>
              <th>{{ t('operator.detail.status') }}</th>
              <th>{{ t('operator.detail.employee') }}</th>
              <th>{{ t('operator.detail.stage') }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="e in [...openDowntimeEvents, ...closedDowntimeEvents]" :key="e.id">
              <td>{{ e.reason_name }}</td>
              <td>{{ e.end_time ? t('operator.detail.closedStatus') : t('operator.detail.openStatus') }}</td>
              <td>{{ e.reported_by }}</td>
              <td>{{ formatDisplayDateTime(e.start_time) || '—' }}</td>
              <td>
                <button v-if="!e.end_time" class="link-action danger" @click="closeEvent(e)">{{ t('operator.detail.close') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- Faults -->
      <section class="panel">
        <div class="panel-header">
          <h3>{{ t('operator.detail.faults') }}</h3>
          <button class="btn-secondary" @click="openFault">{{ t('operator.detail.reportFault') }}</button>
        </div>
        <div class="mini-stats">
          <span>{{ t('operator.detail.openItems', { count: openFaults.length }) }}</span>
          <span>{{ t('operator.detail.resolvedItems', { count: resolvedFaults.length }) }}</span>
        </div>
        <div v-if="faults.length === 0" class="empty-inline">{{ t('operator.detail.noFaults') }}</div>
        <table v-else class="data-table">
          <thead>
            <tr>
              <th>{{ t('operator.detail.description') }}</th>
              <th>{{ t('operator.detail.status') }}</th>
              <th>{{ t('operator.detail.employee') }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="f in [...openFaults, ...resolvedFaults]" :key="f.id">
              <td>{{ f.description }}</td>
              <td>
                <span :class="['status-badge', f.status === 'open' ? 'status-open' : 'status-resolved']">{{ f.status === 'open' ? t('operator.detail.openStatus') : t('operator.detail.resolvedStatus') }}</span>
              </td>
              <td>{{ f.reported_by }}</td>
              <td>
                <button v-if="f.status === 'open'" class="link-action" @click="resolveFault(f)">{{ t('operator.detail.resolve') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- Maintenance -->
      <section class="panel">
        <div class="panel-header">
          <h3>{{ t('operator.detail.maintenance') }}</h3>
          <button class="btn-secondary" @click="openMaintenance">{{ t('operator.detail.requestMaintenance') }}</button>
        </div>
        <div class="mini-stats">
          <span>{{ t('operator.detail.openItems', { count: openMaintenanceRequests.length }) }}</span>
          <span>{{ t('operator.detail.doneItems', { count: doneMaintenanceRequests.length }) }}</span>
        </div>
        <div v-if="maintenanceRequests.length === 0" class="empty-inline">{{ t('operator.detail.noMaintenance') }}</div>
        <table v-else class="data-table">
          <thead>
            <tr>
              <th>{{ t('operator.detail.reason') }}</th>
              <th>{{ t('operator.detail.priority') }}</th>
              <th>{{ t('operator.detail.status') }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="m in [...openMaintenanceRequests, ...doneMaintenanceRequests]" :key="m.id">
              <td>{{ m.reason }}</td>
              <td>{{ t(`operator.detail.priority${m.priority.charAt(0).toUpperCase()}${m.priority.slice(1)}`) }}</td>
              <td>{{ m.status === 'in_progress' ? t('operator.detail.statusInProgress') : m.status === 'done' ? t('operator.detail.statusDone') : t('operator.detail.openStatus') }}</td>
              <td>
                <button v-if="m.status !== 'done'" class="link-action" @click="advanceStatus(m)">{{ t('operator.detail.advance') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>

    <!-- Counter record button -->
    <div class="counter-bar">
      <span>{{ t('operator.detail.counterHint', { total: machine.counter_total.toLocaleString(), shift: machine.counter_shift.toLocaleString() }) }}</span>
      <button class="btn-primary" @click="openCounter">{{ t('operator.detail.recordCounter') }}</button>
    </div>

    <!-- Start run modal -->
    <div v-if="showStartRun" class="modal-overlay" @click.self="showStartRun = false">
      <form class="modal" @submit.prevent="submitStartRun">
        <h3>{{ t('operator.run.startRun') }}</h3>
        <p class="modal-sub">{{ machine?.name ?? '—' }} · {{ t('operator.run.manualOrderHint') }}</p>
        <label>{{ t('operator.run.order') }}
          <select v-model.number="startRunForm.order_id" :disabled="orderChoicesLoading" @change="onStartRunOrderChange">
            <option :value="0" disabled>{{ orderChoicesLoading ? t('operator.run.loadingOrders') : t('operator.run.selectOrder') }}</option>
            <option v-for="choice in orderChoices" :key="choice.id" :value="choice.id">
              {{ choice.mo_code }} · {{ choice.product_name ?? `#${choice.product_id}` }}
            </option>
          </select>
        </label>
        <label>{{ t('operator.run.variant') }}
          <select v-model.number="startRunForm.variant_id" :disabled="!startRunVariants.length" @change="onStartRunVariantChange">
            <option :value="0" disabled>{{ startRunVariants.length ? t('operator.run.selectVariant') : t('operator.run.noVariants') }}</option>
            <option v-for="variant in startRunVariants" :key="variant.id" :value="variant.id">{{ variantOptionLabel(variant) }}</option>
          </select>
        </label>
        <label>{{ t('operator.run.stage') }}
          <select v-model="startRunForm.stage_code">
            <option value="" disabled>{{ t('operator.run.selectStage') }}</option>
            <option v-for="stage in startRunStages" :key="stage.id" :value="stage.stage_code">{{ stage.stage_name }}</option>
          </select>
        </label>
        <label>{{ t('operator.run.plannedQuantity') }}
          <input v-model.number="startRunForm.planned_quantity" type="number" min="0" step="1" />
        </label>
        <label>{{ t('operator.run.targetRate') }}
          <input v-model="startRunForm.target_rate_per_hour" type="number" min="0" step="0.01" />
        </label>
        <label>{{ t('operator.run.shift') }}
          <select v-model="startRunForm.shift">
            <option value="day">{{ t('operator.run.day') }}</option>
            <option value="night">{{ t('operator.run.night') }}</option>
          </select>
        </label>
        <label>{{ t('operator.detail.notes') }}
          <input v-model="startRunForm.notes" type="text" />
        </label>
        <small v-if="actionError" class="error">{{ actionError }}</small>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showStartRun = false">{{ t('operator.detail.cancel') }}</button>
          <button type="submit" class="btn-primary" :disabled="submitting">
            {{ submitting ? t('operator.run.submitting') : t('operator.run.startRun') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Changeover modal -->
    <div v-if="showChangeover" class="modal-overlay" @click.self="showChangeover = false">
      <form class="modal" @submit.prevent="submitChangeover">
        <h3>{{ t('operator.run.changeover') }}</h3>
        <p class="modal-sub">{{ currentRun?.run_code ?? '—' }} · {{ runVariantLabel(currentRun) }}</p>
        <label>{{ t('operator.run.order') }}
          <select v-model.number="changeoverForm.order_id" :disabled="orderChoicesLoading" @change="onChangeoverOrderChange">
            <option :value="0" disabled>{{ orderChoicesLoading ? t('operator.run.loadingOrders') : t('operator.run.selectOrder') }}</option>
            <option v-for="choice in orderChoices" :key="choice.id" :value="choice.id">
              {{ choice.mo_code }} · {{ choice.product_name ?? `#${choice.product_id}` }}
            </option>
          </select>
        </label>
        <label>{{ t('operator.run.variant') }}
          <select v-model.number="changeoverForm.variant_id" :disabled="!changeoverVariants.length">
            <option :value="0" disabled>{{ changeoverVariants.length ? t('operator.run.selectVariant') : t('operator.run.noVariants') }}</option>
            <option v-for="variant in changeoverVariants" :key="variant.id" :value="variant.id">{{ variantOptionLabel(variant) }}</option>
          </select>
        </label>
        <label>{{ t('operator.run.stage') }}
          <select v-model="changeoverForm.stage_code">
            <option value="" disabled>{{ t('operator.run.selectStage') }}</option>
            <option v-for="stage in changeoverStages" :key="stage.id" :value="stage.stage_code">{{ stage.stage_name }}</option>
          </select>
        </label>
        <label>{{ t('operator.run.targetRate') }}
          <input v-model="changeoverForm.target_rate_per_hour" type="number" min="0" step="0.01" />
        </label>
        <label>{{ t('operator.run.closeReason') }}
          <select v-model="changeoverForm.close_reason">
            <option v-for="reason in CLOSE_REASONS" :key="reason" :value="reason">{{ closeReasonLabel(reason) }}</option>
          </select>
        </label>
        <label>{{ t('operator.detail.notes') }}
          <input v-model="changeoverForm.notes" type="text" />
        </label>
        <small v-if="actionError" class="error">{{ actionError }}</small>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showChangeover = false">{{ t('operator.detail.cancel') }}</button>
          <button type="submit" class="btn-primary" :disabled="submitting">
            {{ submitting ? t('operator.run.submitting') : t('operator.run.changeover') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Close run modal -->
    <div v-if="showCloseRun" class="modal-overlay" @click.self="showCloseRun = false">
      <form class="modal" @submit.prevent="submitCloseRun">
        <h3>{{ t('operator.run.closeRun') }}</h3>
        <p class="modal-sub">{{ currentRun?.run_code ?? '—' }} · {{ runVariantLabel(currentRun) }}</p>
        <label>{{ t('operator.run.closeReason') }}
          <select v-model="closeRunForm.close_reason">
            <option v-for="reason in CLOSE_REASONS" :key="reason" :value="reason">{{ closeReasonLabel(reason) }}</option>
          </select>
        </label>
        <label>{{ t('operator.run.closingCounter') }}
          <input v-model="closeRunForm.closing_counter_total" type="number" min="0" step="1" />
        </label>
        <label>{{ t('operator.detail.notes') }}
          <input v-model="closeRunForm.notes" type="text" />
        </label>
        <small v-if="actionError" class="error">{{ actionError }}</small>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showCloseRun = false">{{ t('operator.detail.cancel') }}</button>
          <button type="submit" class="btn-primary" :disabled="submitting">
            {{ submitting ? t('operator.run.submitting') : t('operator.run.closeRun') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Downtime modal -->
    <div v-if="showDowntime" class="modal-overlay" @click.self="showDowntime = false">
      <form class="modal" @submit.prevent="submitDowntime">
        <h3>{{ t('operator.detail.reportDowntime') }}</h3>
        <p class="modal-sub">{{ order?.mo_code ?? '—' }} · {{ assignedLot || '—' }}</p>
        <label>{{ t('operator.detail.selectReason') }}
          <select v-model="downtimeForm.reason_code" required>
            <option v-for="r in downtimeReasons" :key="r.code" :value="r.code">{{ r.name }}</option>
          </select>
        </label>
        <label>{{ t('operator.detail.notes') }}
          <input v-model="downtimeForm.notes" type="text" />
        </label>
        <small v-if="actionError" class="error">{{ actionError }}</small>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showDowntime = false">{{ t('operator.detail.cancel') }}</button>
          <button type="submit" class="btn-primary" :disabled="submitting">
            {{ submitting ? t('operator.detail.reporting') : t('operator.detail.report') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Setup modal -->
    <div v-if="showSetup" class="modal-overlay" @click.self="showSetup = false">
      <form class="modal" @submit.prevent="submitSetup">
        <h3>{{ t('operator.detail.saveSetup') }}</h3>
        <p class="modal-sub">{{ order?.mo_code ?? '—' }} · {{ assignedLot || '—' }}</p>
        <div v-for="field in setupForm" :key="field.code" class="setup-row">
          <label>{{ setupLabel(field.code) }}</label>
          <input v-model.trim="field.value" type="text" required />
        </div>
        <label>{{ t('operator.detail.notes') }}
          <input v-model="setupNotes" type="text" :placeholder="t('operator.detail.setupNotesPlaceholder')" />
        </label>
        <small v-if="actionError" class="error">{{ actionError }}</small>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showSetup = false">{{ t('operator.detail.cancel') }}</button>
          <button type="submit" class="btn-primary" :disabled="submitting">
            {{ submitting ? t('operator.detail.saving') : t('operator.detail.save') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Fault modal -->
    <div v-if="showFault" class="modal-overlay" @click.self="showFault = false">
      <form class="modal" @submit.prevent="submitFault">
        <h3>{{ t('operator.detail.reportFault') }}</h3>
        <label>{{ t('operator.detail.faultCode') }}
          <input v-model="faultForm.fault_code" type="text" required />
        </label>
        <label>{{ t('operator.detail.description') }}
          <input v-model="faultForm.description" type="text" :placeholder="t('operator.detail.reportFaultPlaceholder')" required />
        </label>
        <small v-if="actionError" class="error">{{ actionError }}</small>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showFault = false">{{ t('operator.detail.cancel') }}</button>
          <button type="submit" class="btn-primary" :disabled="submitting">
            {{ submitting ? t('operator.detail.saving') : t('operator.detail.report') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Maintenance modal -->
    <div v-if="showMaintenance" class="modal-overlay" @click.self="showMaintenance = false">
      <form class="modal" @submit.prevent="submitMaintenance">
        <h3>{{ t('operator.detail.requestMaintenance') }}</h3>
        <label>{{ t('operator.detail.reason') }}
          <input v-model="maintenanceForm.reason" type="text" required />
        </label>
        <label>{{ t('operator.detail.priority') }}
          <select v-model="maintenanceForm.priority">
            <option value="low">{{ t('operator.detail.priorityLow') }}</option>
            <option value="medium">{{ t('operator.detail.priorityMedium') }}</option>
            <option value="high">{{ t('operator.detail.priorityHigh') }}</option>
          </select>
        </label>
        <label>{{ t('operator.detail.notes') }}
          <input v-model="maintenanceForm.notes" type="text" />
        </label>
        <small v-if="actionError" class="error">{{ actionError }}</small>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showMaintenance = false">{{ t('operator.detail.cancel') }}</button>
          <button type="submit" class="btn-primary" :disabled="submitting">
            {{ submitting ? t('operator.detail.saving') : t('operator.detail.requestMaintenance') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Counter modal -->
    <div v-if="showCounter" class="modal-overlay" @click.self="showCounter = false">
      <form class="modal" @submit.prevent="submitCounter">
        <h3>{{ t('operator.detail.recordCounter') }}</h3>
        <p class="modal-sub">{{ t('operator.detail.counterDelta', { total: counterDelta.total.toLocaleString(), shift: counterDelta.shift.toLocaleString() }) }}</p>
        <label>{{ t('operator.detail.counterTotalLabel') }}
          <input v-model.number="counterForm.counter_total" type="number" min="0" required />
        </label>
        <label>{{ t('operator.detail.counterShiftLabel') }}
          <input v-model.number="counterForm.counter_shift" type="number" min="0" required />
        </label>
        <small v-if="actionError" class="error">{{ actionError }}</small>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showCounter = false">{{ t('operator.detail.cancel') }}</button>
          <button type="submit" class="btn-primary" :disabled="submitting">
            {{ submitting ? t('operator.detail.recordingCounter') : t('operator.detail.recordCounter') }}
          </button>
        </div>
      </form>
    </div>
  </template>
</template>

<style scoped>
.page-heading { margin-bottom: 20px; }
.eyebrow {
  margin: 0 0 4px;
  color: #65736e;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
h2 { margin: 0 0 4px; font-size: 22px; font-weight: 700; }
.page-heading p { margin: 0; color: #5f6b6a; font-size: 14px; }
.back {
  display: inline-block;
  margin-bottom: 12px;
  color: #147a8a;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
}
.back:hover { text-decoration: underline; }

.state-message { padding: 40px 0; text-align: center; color: #5f6b6a; }
.state-message.error { color: #b33a3a; }
.empty-inline { color: #8b9a95; font-size: 13px; font-style: italic; }
.error { color: #b33a3a; font-size: 12px; }
.inline-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #efb5ad;
  border-radius: 8px;
  background: #fff3f1;
  color: #852d22;
  font-size: 13px;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}
.summary-card {
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  padding: 16px 20px;
  text-align: center;
}
.summary-card.attention { border-color: #f0c040; background: #fffdf5; }
.summary-value { display: block; font-size: 22px; font-weight: 800; color: #1a2b26; }
.summary-label { display: block; font-size: 11px; color: #65736e; text-transform: uppercase; font-weight: 600; margin-top: 2px; }

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.panel {
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 12px;
  padding: 20px;
}
.panel h3 { margin: 0 0 12px; font-size: 15px; font-weight: 700; color: #1a2b26; }
.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.panel-header h3 { margin: 0; }

.order-code { font-size: 16px; font-weight: 800; color: #1a2b26; font-family: monospace; margin: 0; }
.order-meta { margin: 4px 0 0; font-size: 13px; color: #65736e; }
.progress-stack { display: grid; gap: 8px; margin-top: 14px; }
.progress-head, .order-stats {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  color: #65736e;
  font-size: 12px;
}
.progress-head strong, .order-stats strong { color: #1a2b26; }
.progress-stack progress {
  width: 100%;
  height: 10px;
  overflow: hidden;
  border: 0;
  border-radius: 999px;
  background: #edf2f0;
}
.progress-stack progress::-webkit-progress-bar { background: #edf2f0; }
.progress-stack progress::-webkit-progress-value { background: #147a8a; }
.progress-stack progress::-moz-progress-bar { background: #147a8a; }

.workflow-panel { border-left: 4px solid #147a8a; }
.workflow-blocked { border-left-color: #b33a3a; background: #fffafa; }
.workflow-missingLot, .workflow-setup { border-left-color: #d49a22; background: #fffdf5; }
.workflow-noOrder { border-left-color: #8b9a95; }
.workflow-badge {
  flex: 0 0 auto;
  padding: 4px 10px;
  border-radius: 999px;
  background: #eaf0ed;
  color: #3d5a4f;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}
.workflow-ready .workflow-badge { background: #dcefe7; color: #1f5c4b; }
.workflow-blocked .workflow-badge { background: #fde8e8; color: #b33a3a; }
.workflow-missingLot .workflow-badge, .workflow-setup .workflow-badge { background: #fef3cd; color: #856404; }
.workflow-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.workflow-grid div {
  display: grid;
  gap: 3px;
  padding: 10px;
  border: 1px solid #eef2f0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.7);
}
.workflow-grid span { color: #65736e; font-size: 11px; font-weight: 700; text-transform: uppercase; }
.workflow-grid strong { color: #1a2b26; font-size: 13px; }
.workflow-hint { margin: 12px 0 0; color: #53625d; font-size: 13px; line-height: 1.5; }

.plain-list { list-style: none; margin: 0; padding: 0; }
.plain-list li {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f2f1;
  font-size: 13px;
  color: #1a2b26;
}
.plain-list li:last-child { border-bottom: none; }
.plain-list span { color: #65736e; font-size: 12px; }

.placeholder-note {
  color: #8b9a95;
  font-size: 12px;
  font-style: italic;
  margin: 0;
  background: #f6f8f7;
  border: 1px dashed #e2e6e3;
  border-radius: 8px;
  padding: 12px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}
.data-table th {
  text-align: left;
  padding: 10px 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #65736e;
  background: #f6f8f7;
  border-bottom: 1px solid #e2e6e3;
}
.data-table td {
  padding: 10px 12px;
  font-size: 13px;
  color: #1a2b26;
  border-bottom: 1px solid #f0f2f1;
}
.data-table tr:last-child td { border-bottom: none; }

.action-message {
  padding: 10px 14px;
  background: #dcefe7;
  color: #1f5c4b;
  border-radius: 8px;
  font-size: 13px;
  margin: 8px 0;
}

.link-action {
  color: #147a8a;
  font-weight: 600;
  font-size: 12px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}
.link-action:hover { text-decoration: underline; }
.link-action.danger { color: #b33a3a; }

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 20, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}
.modal {
  width: 380px;
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.modal h3 { margin: 0; font-size: 16px; font-weight: 700; }
.modal label {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 600;
  color: #65736e;
  gap: 4px;
}
.modal input, .modal select {
  padding: 10px 12px;
  border: 1px solid #d4d8d5;
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.btn-primary {
  display: inline-block;
  padding: 8px 20px;
  background: #147a8a;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.btn-primary:disabled { opacity: 0.5; cursor: default; }
.btn-primary:hover:not(:disabled) { background: #0f6573; }
.btn-secondary {
  padding: 8px 16px;
  background: #fff;
  color: #147a8a;
  border: 1px solid #147a8a;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.mini-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 12px;
}
.mini-stats span {
  padding: 5px 9px;
  border-radius: 999px;
  background: #f1f5f3;
  color: #53625d;
  font-size: 12px;
  font-weight: 700;
}
.btn-secondary:hover { background: #f0f7f8; }
.btn-secondary:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-secondary:disabled:hover { background: #fff; }

.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: #eaf0ed;
  color: #3d5a4f;
}
.status-open { background: #fde8e8; color: #b33a3a; }
.status-resolved { background: #dcefe7; color: #1f5c4b; }

.counter-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
}
.counter-bar span { color: #65736e; font-size: 13px; }

.setup-row {
  display: flex;
  gap: 12px;
  align-items: center;
}
.setup-row label {
  width: 120px;
  font-size: 12px;
  font-weight: 700;
  color: #65736e;
  text-transform: uppercase;
  margin: 0;
}
.setup-row input {
  flex: 1;
}
.setup-context, .modal-sub {
  margin: 0 0 12px;
  color: #65736e;
  font-size: 12px;
}
.setup-context {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.setup-context strong, .modal-sub strong { color: #1a2b26; }
.material-totals {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 10px 0 12px;
}
.material-totals span {
  padding: 7px 10px;
  border: 1px solid #d7e6df;
  border-radius: 8px;
  background: #f6fbf8;
  color: #53625d;
  font-size: 12px;
}
.material-totals strong { color: #1f5c4b; }
.material-table { margin-top: 10px; }

@media (max-width: 900px) {
  .summary-row { grid-template-columns: repeat(2, 1fr); }
  .detail-grid { grid-template-columns: 1fr; }
  .order-stats { flex-wrap: wrap; }
  .workflow-grid { grid-template-columns: 1fr; }
}
</style>

<style scoped>
/* Machine run panel (MACHINE_RUNS_API.md) */
.run-panel { grid-column: 1 / -1; }
.run-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.run-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px 16px; margin-bottom: 14px; }
.run-grid div { display: grid; gap: 2px; }
.run-grid span { color: #65736e; font-size: 11px; font-weight: 700; text-transform: uppercase; }
.run-grid strong { color: #1a2b26; font-size: 14px; }
.state-bar { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 14px; padding: 12px; border: 1px solid #e2e6e3; border-radius: 8px; background: #f7faf8; }
.state-bar > div { display: grid; gap: 2px; }
.state-bar small { color: #65736e; }
.state-form { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.state-form select, .state-form input { padding: 8px 10px; border: 1px solid #cbd7d2; border-radius: 8px; font: inherit; }
.run-heading { margin: 16px 0 8px; color: #1a2b26; font-size: 13px; }
.run-pager { display: flex; align-items: center; gap: 10px; margin-top: 8px; color: #65736e; font-size: 12px; }
</style>
